import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Loader2 } from "lucide-react";
import TaskCard, { type ChallengeTask } from "./TaskCard";
import CreateTaskDialog from "./CreateTaskDialog";
import AITaskGenerator from "./AITaskGenerator";
import { Skeleton } from "@/components/ui/skeleton";

interface TaskListProps {
  challengeId: string;
  challengeTitle: string;
  problemStatement: string | null;
  canManage: boolean;
  isTeamMember: boolean;
}

const TaskList: React.FC<TaskListProps> = ({
  challengeId,
  challengeTitle,
  problemStatement,
  canManage,
  isTeamMember,
}) => {
  const [tasks, setTasks] = useState<ChallengeTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ChallengeTask | null>(null);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTasks();
    getCurrentUser();
  }, [challengeId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchTasks = async () => {
    try {
      // Fetch tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from("challenge_tasks")
        .select("*")
        .eq("challenge_id", challengeId)
        .order("order_index", { ascending: true });

      if (tasksError) throw tasksError;

      // Fetch claims for these tasks
      const taskIds = tasksData?.map((t) => t.id) || [];
      
      let claimsMap: Record<string, any> = {};
      
      if (taskIds.length > 0) {
        const { data: claimsData, error: claimsError } = await supabase
          .from("task_claims")
          .select("*, profiles:user_id(email)")
          .in("task_id", taskIds)
          .in("status", ["active", "submitted"]);

        if (claimsError) throw claimsError;

        claimsData?.forEach((claim) => {
          claimsMap[claim.task_id] = {
            id: claim.id,
            user_id: claim.user_id,
            status: claim.status,
            user_email: (claim.profiles as any)?.email,
          };
        });
      }

      // Merge tasks with claims
      const tasksWithClaims = (tasksData || []).map((task) => ({
        ...task,
        claim: claimsMap[task.id] || null,
      })) as ChallengeTask[];

      setTasks(tasksWithClaims);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (data: {
    title: string;
    description?: string;
    estimated_hours?: number;
    difficulty: string;
    skill_tags: string[];
  }) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (editingTask) {
        // Update existing task
        const { error } = await supabase
          .from("challenge_tasks")
          .update({
            title: data.title,
            description: data.description,
            estimated_hours: data.estimated_hours,
            difficulty: data.difficulty,
            skill_tags: data.skill_tags,
          })
          .eq("id", editingTask.id);

        if (error) throw error;
        toast.success("Task updated");
      } else {
        // Create new task
        const { error } = await supabase.from("challenge_tasks").insert({
          challenge_id: challengeId,
          title: data.title,
          description: data.description,
          estimated_hours: data.estimated_hours,
          difficulty: data.difficulty,
          skill_tags: data.skill_tags,
          order_index: tasks.length,
          created_by: user?.id,
        });

        if (error) throw error;
        toast.success("Task created");
      }

      setDialogOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task");
    } finally {
      setSaving(false);
    }
  };

  const handleClaim = async (taskId: string) => {
    if (!currentUserId) {
      toast.error("Please sign in to claim tasks");
      return;
    }

    setClaiming(taskId);
    try {
      // Create claim
      const { error: claimError } = await supabase
        .from("task_claims")
        .insert({
          task_id: taskId,
          user_id: currentUserId,
          status: "active",
        });

      if (claimError) throw claimError;

      // Update task status
      const { error: updateError } = await supabase
        .from("challenge_tasks")
        .update({ status: "claimed" })
        .eq("id", taskId);

      if (updateError) throw updateError;

      toast.success("Task claimed!");
      fetchTasks();
    } catch (error) {
      console.error("Error claiming task:", error);
      toast.error("Failed to claim task");
    } finally {
      setClaiming(null);
    }
  };

  const handleUnclaim = async (taskId: string) => {
    try {
      // Delete claim
      const { error: deleteError } = await supabase
        .from("task_claims")
        .delete()
        .eq("task_id", taskId)
        .eq("user_id", currentUserId!);

      if (deleteError) throw deleteError;

      // Update task status
      const { error: updateError } = await supabase
        .from("challenge_tasks")
        .update({ status: "open" })
        .eq("id", taskId);

      if (updateError) throw updateError;

      toast.success("Task unclaimed");
      fetchTasks();
    } catch (error) {
      console.error("Error unclaiming task:", error);
      toast.error("Failed to unclaim task");
    }
  };

  const handleSubmit = async (taskId: string) => {
    try {
      // Update claim to submitted
      const { error: claimError } = await supabase
        .from("task_claims")
        .update({ status: "submitted" })
        .eq("task_id", taskId)
        .eq("user_id", currentUserId!);

      if (claimError) throw claimError;

      // Update task status
      const { error: updateError } = await supabase
        .from("challenge_tasks")
        .update({ status: "in_review" })
        .eq("id", taskId);

      if (updateError) throw updateError;

      toast.success("Task submitted for review");
      fetchTasks();
    } catch (error) {
      console.error("Error submitting task:", error);
      toast.error("Failed to submit task");
    }
  };

  const handleApprove = async (taskId: string) => {
    try {
      const task = tasks.find((t) => t.id === taskId);
      
      // Update claim to approved
      const { error: claimError } = await supabase
        .from("task_claims")
        .update({ 
          status: "approved",
          completed_at: new Date().toISOString()
        })
        .eq("task_id", taskId)
        .eq("user_id", task?.claim?.user_id!);

      if (claimError) throw claimError;

      // Update task status
      const { error: updateError } = await supabase
        .from("challenge_tasks")
        .update({ status: "complete" })
        .eq("id", taskId);

      if (updateError) throw updateError;

      toast.success("Task approved and completed!");
      fetchTasks();
    } catch (error) {
      console.error("Error approving task:", error);
      toast.error("Failed to approve task");
    }
  };

  const handleReject = async (taskId: string) => {
    try {
      const task = tasks.find((t) => t.id === taskId);
      
      // Update claim to rejected
      const { error: claimError } = await supabase
        .from("task_claims")
        .update({ status: "rejected" })
        .eq("task_id", taskId)
        .eq("user_id", task?.claim?.user_id!);

      if (claimError) throw claimError;

      // Reopen task
      const { error: updateError } = await supabase
        .from("challenge_tasks")
        .update({ status: "open" })
        .eq("id", taskId);

      if (updateError) throw updateError;

      toast.success("Task rejected and reopened");
      fetchTasks();
    } catch (error) {
      console.error("Error rejecting task:", error);
      toast.error("Failed to reject task");
    }
  };

  const handleEdit = (task: ChallengeTask) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const openTasks = tasks.filter((t) => t.status === "open");
  const claimedTasks = tasks.filter((t) => t.status === "claimed");
  const inReviewTasks = tasks.filter((t) => t.status === "in_review");
  const completeTasks = tasks.filter((t) => t.status === "complete");

  return (
    <div className="space-y-4">
      {/* Actions */}
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => {
              setEditingTask(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Task
          </Button>
          <AITaskGenerator
            challengeId={challengeId}
            challengeTitle={challengeTitle}
            problemStatement={problemStatement}
            onTasksGenerated={fetchTasks}
          />
        </div>
      )}

      {/* Task Summary */}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span>{openTasks.length} open</span>
        <span>{claimedTasks.length} in progress</span>
        <span>{inReviewTasks.length} in review</span>
        <span>{completeTasks.length} complete</span>
      </div>

      {/* Task List */}
      {tasks.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
          <p>No tasks yet.</p>
          {canManage && (
            <p className="text-sm mt-2">
              Add tasks manually or use AI to generate them from the problem brief.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              currentUserId={currentUserId}
              canManage={canManage}
              onClaim={handleClaim}
              onUnclaim={handleUnclaim}
              onSubmit={handleSubmit}
              onApprove={handleApprove}
              onReject={handleReject}
              onEdit={canManage ? handleEdit : undefined}
              claiming={claiming === task.id}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <CreateTaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleCreateTask}
        editingTask={editingTask}
        loading={saving}
      />
    </div>
  );
};

export default TaskList;
